/**
 * Pure close/exit sequencing for the {@link ./CardSheet}, split out so the timing rules are
 * unit-testable without a DOM.
 *
 * The sheet closes with an exit animation and DEFERS the caller's `onExited` until the transform (or
 * opacity, under reduced motion) transition ends — so a route change or an unmount never yanks the
 * sheet mid-slide — with a fallback timer in case `transitionend` never fires.
 *
 * This began as the FeaturePanel's own module (ui#150) and was generalized in ui#1054, when the
 * Personalise editor moved onto the same card sheet: two surfaces sharing one presentation must share
 * its timing, or "when is the exit finished" gets answered twice and drifts.
 */

/** The sheet's animation state: waiting off-screen, settled open, or animating out on close. */
export type SheetState = "closed" | "open" | "closing";

/**
 * Safety-net timeout (ms) for the deferred exit: if `transitionend` never fires (interrupted
 * transition, a browser that drops the event, reduced-motion edge cases), run the caller's `onExited`
 * anyway this long after the close was requested. Comfortably longer than the 180ms exit / 120ms
 * reduced timings so it only ever acts as a fallback, never pre-empts the real animation.
 */
export const SHEET_CLOSE_FALLBACK_MS = 250;

/**
 * Whether a signal that the exit animation is over may finish the close — asked by BOTH signals, the
 * real `transitionend` and the fallback timer behind it, so the two can never answer it differently.
 *
 * A close is only finishable while one is actually playing AND the caller still wants the sheet shut.
 * The second half is ui#2407 and is not redundant: the enter runs off a frame callback, so a sheet
 * REOPENED during its own slide out spends a moment at {@code closing} with {@code open} already true,
 * and the previous exit's `transitionend` lands in exactly that window. Finishing there marks the sheet
 * no longer raised while it is on its way back IN, and {@link sheetExitStep} then answers {@code hold}
 * to its next close — the card stays on screen for good, with the caller never told.
 */
export function sheetCloseMayFinish(open: boolean, state: SheetState): boolean {
	return !open && state === "closing";
}

/**
 * Whether a `transitionend` fired while the sheet is closing should trigger the deferred exit.
 *
 * Only the compositor properties the sheet actually animates count (`transform` normally, `opacity`
 * under reduced motion); bubbled transitions from descendant elements — a hovered button, the body
 * crossfade, a shadow easing on focus — must NOT be mistaken for the sheet's own exit finishing.
 */
export function isSheetCloseTransition(open: boolean, state: SheetState, propertyName: string): boolean {
	return sheetCloseMayFinish(open, state)
		&& (propertyName === "transform" || propertyName === "opacity");
}

/**
 * What a close still owes the caller.
 *
 *  - `hold` — nothing to do here: the sheet is open, was never raised, or is already sliding out;
 *  - `animate` — play the exit, and tell the caller when the transition (or the fallback) ends;
 *  - `finish` — the exit is already over, so tell the caller now.
 */
export type SheetExitStep = "hold" | "animate" | "finish";

/**
 * Which of those a close asks for, from the caller's `open`, the sheet's animation state, and whether
 * this sheet was ever raised.
 *
 * `finish` is the case ui#2236 was, and the reason this is a function rather than one condition. The
 * enter runs off a frame callback, so every sheet spends its first frame at `closed` — off screen,
 * still sliding in, but already answering Escape and the outside click that dismisses it. A dismissal
 * there has nothing to slide out, and treating it as `hold` dropped the card and told the caller
 * nothing: for the feature sheet, whose `onExited` is what navigates home, the sender's click made the
 * sheet vanish and left them on the route it had been covering.
 *
 * `wasRaised` is what keeps that apart from a sheet merely RENDERED closed — the ordinary state of a
 * caller that keeps one mounted and toggles `open` — which owes nobody an exit.
 */
export function sheetExitStep(open: boolean, state: SheetState, wasRaised: boolean): SheetExitStep {
	if (open || !wasRaised || state === "closing") {
		return "hold";
	}
	return state === "open" ? "animate" : "finish";
}
